/** Inline SVG polyline of `counts.series` activeSubscribers (TMMPDM-0020). */
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { useCopy } from '@/hooks/useCopy';
import { EmptyState, SectionCard } from '@/components/page';
import type { Period } from './period';
import type { FleetData } from './useFleetDashboard';

const HEIGHT = 160;

function ChartSurface({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <SectionCard title={title} headingLevel="h3">
        {children}
      </SectionCard>
    </Box>
  );
}

interface ActiveSubscribersChartProps {
  data: FleetData | null;
  loading: boolean;
  period: Period;
  locale: string;
}

export function ActiveSubscribersChart({ data, loading, period }: ActiveSubscribersChartProps) {
  const { t } = useCopy();
  const theme = useTheme();

  if (!data) {
    if (!loading) return null;
    return (
      <ChartSurface title={t('dashboard.chart.subscribersTitle', 'Active subscribers, daily')}>
        <Typography variant="body2" color="text.secondary">
          {t('common.loading', 'Loading')}
        </Typography>
      </ChartSurface>
    );
  }

  const series = data.dashboard.counts.series;
  if (series.length === 0) {
    return (
      <ChartSurface title={t('dashboard.chart.subscribersTitle', 'Active subscribers, daily')}>
        <EmptyState title={t('dashboard.chart.empty', 'No activity in this period.')} />
      </ChartSurface>
    );
  }

  const values = series.map((point) => point.activeSubscribers);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pad = 12;
  const innerH = HEIGHT - pad * 2;
  const points = series
    .map((point, index) => {
      const x = series.length === 1 ? 50 : (index / (series.length - 1)) * 100;
      // Display scaling only — y maps the server count into the SVG box.
      const y = pad + ((max - point.activeSubscribers) / span) * innerH;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <ChartSurface title={t('dashboard.chart.subscribersTitle', 'Active subscribers, daily')}>
      <svg
        role="img"
        width="100%"
        height={HEIGHT}
        viewBox={`0 0 100 ${HEIGHT}`}
        preserveAspectRatio="none"
        aria-label={t('dashboard.chart.subscribersAria', 'Active subscribers, daily')}
      >
        <polyline
          fill="none"
          style={{ stroke: (theme.vars ?? theme).palette.primary.main }}
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
          points={points}
        />
      </svg>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          {period.from}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {period.to}
        </Typography>
      </Box>
    </ChartSurface>
  );
}
